import { type Action, combineReducers, configureStore, type ThunkAction } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import authReducer from './auth/auth.reducers';
import filtersReducer from './filters/filters.reducers';
import sideMenuReducer from './sidemenu/sidemenu.reducer';
import { authApi } from '@/services/authApi';
import { countiesApi } from '@/services/countiesApi';
import { fileLibraryApi } from '@/services/fileLibraryApi';
import { hospitalApi } from '@/services/hospitalApi';
import { incidentsApi } from '@/services/incidentsApi';
import { modFaqApi } from '@/services/modFaqApi';
import { notificationsApi } from '@/services/notificationsApi';
import { organizationApi } from '@/services/organizationApi';
import { palmettoApi } from '@/services/palmettoApi';
import { palmettoGroupApi } from '@/services/palmettoGroupApi';
import { patientsApi } from '@/services/patientsApi';
import { regionalCooApi } from '@/services/regionalCooApi';
import { reportsApi } from '@/services/reportsApi';
import { resourceReqApi } from '@/services/resourceReqApi';
import { settingsApi } from '@/services/settingsApi';
import { usersApi } from '@/services/usersApi';

const NODE_ENV = import.meta.env.MODE;

const rootReducer = combineReducers({
  auth: authReducer,
  filters: filtersReducer,
  sideMenu: sideMenuReducer,
  // RTK Query APIs
  [authApi.reducerPath]: authApi.reducer,
  [countiesApi.reducerPath]: countiesApi.reducer,
  [fileLibraryApi.reducerPath]: fileLibraryApi.reducer,
  [hospitalApi.reducerPath]: hospitalApi.reducer,
  [incidentsApi.reducerPath]: incidentsApi.reducer,
  [modFaqApi.reducerPath]: modFaqApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [organizationApi.reducerPath]: organizationApi.reducer,
  [palmettoApi.reducerPath]: palmettoApi.reducer,
  [palmettoGroupApi.reducerPath]: palmettoGroupApi.reducer,
  [patientsApi.reducerPath]: patientsApi.reducer,
  [regionalCooApi.reducerPath]: regionalCooApi.reducer,
  [reportsApi.reducerPath]: reportsApi.reducer,
  [resourceReqApi.reducerPath]: resourceReqApi.reducer,
  [settingsApi.reducerPath]: settingsApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  devTools: NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      authApi.middleware,
      countiesApi.middleware,
      fileLibraryApi.middleware,
      hospitalApi.middleware,
      incidentsApi.middleware,
      modFaqApi.middleware,
      notificationsApi.middleware,
      organizationApi.middleware,
      palmettoApi.middleware,
      palmettoGroupApi.middleware,
      patientsApi.middleware,
      regionalCooApi.middleware,
      reportsApi.middleware,
      resourceReqApi.middleware,
      settingsApi.middleware,
      usersApi.middleware
    ),
});

setupListeners(store.dispatch);
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
// noinspection JSUnusedGlobalSymbols
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action>;
